// src/components/Broadcast/Broadcast.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Button, Form, Card, Modal,  Spinner } from 'react-bootstrap';
import { showTemplates, showContacts, CreateBroadcast, getCompany } from '../../../helpers/api';
import AddNewTemplate from '../Templates/AddNewTemplate';
import '../Templates/AddNewTemplate.css';
import './NewBroadcast.css';
import ShreyuDatepicker from '../../../components/Datepicker';
// import Table from '../../../components/Table';
import { Table } from 'react-bootstrap';

export interface Template {
    template_id: string;
    template_name: string;
    title_type: string;
    title_text: string;
    title_file: string;
    header_variables?: string;
    body?: string;
    footer?: string;
    template_buttons?: Array<{
        type: string;
        text: string;
        url?: string;
        phone_number?: string;
    }>;
}
interface BroadcastData {
    broadcast_name: string;
    template_id: string;
    type: string;
    contacts: string[];
    schedule_date?: string | null;
    schedule_time?: string | null;
}
export interface Contact {
    contact_id: string;
    contact_name: string;
    mobile_no: string;
    group_name: string;
}

const Broadcast: React.FC = () => {
    // =================== STATES ===================
    const [broadcastName, setBroadcastName] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showTemplatePreview, setShowTemplatePreview] = useState(false);
    const [contactOption, setContactOption] = useState<'group' | 'individual'>('group');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [groupFilter, setGroupFilter] = useState<string>('');
    const [dateOption, setDateOption] = useState<'non-schedule' | 'schedule'>('non-schedule');
    const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
    const [scheduleTime, setScheduleTime] = useState('09:00');
    const [currentPage, setCurrentPage] = useState(1);
    const [companyName, setCompanyName] = useState<string>('Loading...');
    const [profile, setProfile] = useState<string>('Loading...');
    const contactsPerPage = 10;

    // =================== FETCH DATA ===================
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [templatesResponse, contactsResponse] = await Promise.all([
                    showTemplates(),
                    showContacts()
                ]);

                setTemplates(templatesResponse.data.templates);
                setContacts(contactsResponse.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);
    const company_id = JSON.parse(sessionStorage.getItem('shreyu_user') || '{}')?.user?.company_id;

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const response = await getCompany(company_id);
                setCompanyName(response.data.company_name || 'Company Name');
                setProfile(response.data.logo || 'profile');
            } catch (err) {
                console.error('Failed to fetch company:', err);
                setCompanyName('Company Name');
            }
        };

        fetchCompany();
    }, []);
    // =================== HELPERS ===================
    const getSelectedTemplateDetails = (): Template | undefined =>
        templates.find(t => t.template_id.toString() === selectedTemplate.toString());

    const handleTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const templateId = e.target.value;
        setSelectedTemplate(templateId);
        setShowTemplatePreview(!!templateId);
    };

    const handleContactSelect = (contact: Contact) => {
        setSelectedContacts(prev =>
            prev.some(c => c.contact_id === contact.contact_id)
                ? prev.filter(c => c.contact_id !== contact.contact_id)
                : [...prev, contact]
        );
    };

    const handleGroupSelect = (groupName: string) => {
        const groupContacts = contacts.filter(contact => contact.group_name === groupName);
        const allGroupContactsSelected = groupContacts.every(contact =>
            selectedContacts.some(c => c.contact_id === contact.contact_id)
        );

        if (allGroupContactsSelected) {
            setSelectedContacts(prev =>
                prev.filter(contact => contact.group_name !== groupName)
            );
        } else {
            const newContacts = groupContacts.filter(
                contact => !selectedContacts.some(c => c.contact_id === contact.contact_id)
            );
            setSelectedContacts(prev => [...prev, ...newContacts]);
        }
    };

    // const getFilteredContacts = () => {
    //     let filtered = contacts;
    //     if (groupFilter) {
    //         filtered = filtered.filter(contact =>
    //             contact.group_name.toLowerCase().includes(groupFilter.toLowerCase()) ||
    //             contact.contact_name.toLowerCase().includes(groupFilter.toLowerCase()) ||
    //             contact.mobile_no.includes(groupFilter)
    //         );
    //     }
    //     return filtered;
    // };
    const getFilteredContacts = () => {
        if (!groupFilter) return contacts;

        const search = groupFilter.toLowerCase();

        return contacts.filter(contact =>
            contact.contact_name?.toLowerCase().includes(search) ||
            contact.mobile_no?.includes(search) ||
            contact.group_name?.toLowerCase().includes(search)
        );
    };


    const uniqueGroups = useMemo(() => {
        const groups = new Set<string>();
        contacts.forEach(contact => groups.add(contact.group_name));
        return Array.from(groups);
    }, [contacts]);

    // =================== PAGINATION ===================
    const filteredContacts = getFilteredContacts();
    const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);
    const indexOfLast = currentPage * contactsPerPage;
    const indexOfFirst = indexOfLast - contactsPerPage;
    const currentContacts = filteredContacts.slice(indexOfFirst, indexOfLast);

    useEffect(() => {
        setCurrentPage(1);
    }, [groupFilter]);

    // =================== SUBMIT HANDLER ===================
    const handleSubmit = async () => {
        if (!broadcastName || !selectedTemplate || selectedContacts.length === 0) {
            alert('Please fill all required fields: Broadcast Name, Template, and at least one contact');
            return;
        }

        const schedule_time = dateOption === "schedule" ? scheduleTime : null;
        const schedule_date =
            dateOption === "schedule" && scheduleDate
                ? scheduleDate.toISOString().split("T")[0]
                : null;

        const broadcastData: BroadcastData = {
            broadcast_name: broadcastName.trim(),
            template_id: selectedTemplate,
            type: dateOption, // schedule or non-schedule
            contacts: selectedContacts.map(c => c.contact_id),
            schedule_date,
            schedule_time,
        };

        try {
            setLoading(true);
            await CreateBroadcast(broadcastData);
            alert("Broadcast created successfully!");
            setBroadcastName("");
            setSelectedTemplate("");
            setSelectedContacts([]);
            setShowPreview(false);
            setShowTemplatePreview(false);
        } catch (error: any) {
            alert(`Failed to create broadcast: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const renderHeaderPreview = (template: Template) => {
        if (!template.title_type || template.title_type === 'none') return null;

        switch (template.title_type.toLowerCase()) {
            case 'text':
                return (
                    <div className="template-text-header fw-bold mb-2">
                        {template.title_text}
                    </div>
                );

            case 'image':
                return (
                    <div className="template-image-header mb-2">
                        <img
                            src={`https://api.skynyx.ai/file_storage/${company_id}/template/${template.title_file}`}
                            alt="Header"
                            style={{ width: "100%", borderRadius: "6px" }}
                        />
                    </div>
                );

            case 'video':
                return (
                    <div className="template-video-header mb-2">
                        <div className="template-video-placeholder">[Video Header]</div>
                    </div>
                );

            case 'document':
                return (
                    <div className="template-document-header mb-2">
                        <div className="template-document-placeholder">
                            📄 Document
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // =================== TEMPLATE PREVIEW ===================
    const renderTemplatePreview = () => {
        const template = getSelectedTemplateDetails();
        if (!template) return null;

        return (
            <Card>
                <Card.Header className="header-bg">
                    <h5 className="mb-0 text-white">Template Preview</h5>
                </Card.Header>

                <Card.Body className="p-0">
                    <div className="whatsapp-preview-container">
                        <div className="whatsapp-preview">

                            {/* Header */}
                            <div className="whatsapp-header-preview">
                                <img
                                    src={`https://api.skynyx.ai/storage/app/public/${profile}`}
                                    className="rounded-circle me-2"
                                    height="40"
                                    alt=""
                                />
                                <div>
                                    <div className="fw-bold">{companyName}</div>
                                    <small className="text-muted">online</small>
                                </div>
                            </div>

                            {/* Message */}
                            <div className="whatsapp-message-preview">
                                {renderHeaderPreview(template)}

                                {template.body && (
                                    <div className="whatsapp-template-body">
                                        {template.body}
                                    </div>
                                )}

                                {template.footer && (
                                    <div className="whatsapp-template-footer text-muted">
                                        {template.footer}
                                    </div>
                                )}

                                {template.template_buttons?.map((btn, i) => (
                                    <div key={i} className="whatsapp-button">
                                        {btn.text}
                                    </div>
                                ))}
                            </div>

                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    // =================== RENDER ===================
    if (loading) return <div className="text-center my-5"><Spinner animation="border" /> Loading...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;

    const allFilteredSelected = currentContacts.length > 0 &&
        currentContacts.every(contact =>
            selectedContacts.some(c => c.contact_id === contact.contact_id)
        );

    return (
        <>

            <Row>
                <Col xs={6}>
                    <Card>
                        <Card.Header className="header-bg d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 text-white">Broadcast</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form>
                                <Row>
                                    <Col xs={12}>

                                        {/* Broadcast Name */}
                                        <Form.Group className="mb-3">
                                            <Form.Label>Broadcast Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={broadcastName}
                                                onChange={(e) => setBroadcastName(e.target.value)}
                                                placeholder="Enter broadcast name"
                                            />
                                        </Form.Group>
                                        {/* Template */}
                                        <Form.Group className="mb-3">
                                            <Form.Label>Templates</Form.Label>
                                            <div className="d-flex">
                                                <Form.Select
                                                    value={selectedTemplate}
                                                    onChange={handleTemplateChange}
                                                >
                                                    <option value="">Select a template</option>
                                                    {templates.map(template => (
                                                        <option key={template.template_id} value={template.template_id}>
                                                            {template.template_name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Button
                                                    variant="outline-info"
                                                    className="ms-2"
                                                    onClick={() => setShowTemplateModal(true)}
                                                >
                                                    + New
                                                </Button>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={8}>

                                        {/* Contact Selection */}
                                        <Form.Group className="mb-3">
                                            <Form.Label>Contacts</Form.Label>
                                            <div className="d-flex flex-wrap gap-2 mb-3">
                                                {uniqueGroups.map(group => {
                                                    const groupContacts = contacts.filter(c => c.group_name === group);
                                                    const allSelected = groupContacts.every(contact =>
                                                        selectedContacts.some(c => c.contact_id === contact.contact_id)
                                                    );
                                                    return (
                                                        <Button
                                                            key={group}
                                                            variant={allSelected ? 'info' : 'outline-info'}
                                                            size="sm"
                                                            onClick={() => handleGroupSelect(group)}
                                                        >
                                                            {group} ({groupContacts.length})
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                            <Button
                                                variant="outline-info"
                                                onClick={() => setShowContactModal(true)}
                                            >
                                                View / Select Contacts
                                            </Button>
                                        </Form.Group>

                                        {/* Schedule Options */}
                                        {/* <div className="mb-3"> */}
                                        <Form.Group className="mb-3">
                                            <Row>
                                                <Col>
                                                    <Form.Check
                                                        inline
                                                        label="Send Now"
                                                        checked={dateOption === 'non-schedule'}
                                                        onChange={() => setDateOption('non-schedule')}
                                                    />
                                                </Col>
                                                <Col>
                                                    <Form.Check
                                                        inline
                                                        label="Schedule"
                                                        checked={dateOption === 'schedule'}
                                                        onChange={() => setDateOption('schedule')}
                                                    />
                                                </Col>
                                            </Row>
                                        </Form.Group>
                                        {/* </div> */}

                                        {/* Date & Time Inputs */}

                                        {dateOption === 'schedule' && (
                                            <Row className="mb-3">
                                                <Col md={8}>
                                                    <Form.Group>
                                                        <Form.Label>Date</Form.Label>
                                                        {/* <Form.Control
                                                            type="date"
                                                            value={scheduleDate}
                                                            onChange={(e) => setScheduleDate(e.target.value)}
                                                            min={new Date().toISOString().split('T')[0]}
                                                        /> */}
                                                        <ShreyuDatepicker
                                                            hideAddon
                                                            dateFormat="dd-MM-yyyy"
                                                            value={scheduleDate}
                                                            onChange={(date: Date) => setScheduleDate(date)}
                                                        // min={new Date().toISOString().split("T")[0]}
                                                        />


                                                    </Form.Group>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Group>
                                                        <Form.Label>Time</Form.Label>
                                                        <Form.Control
                                                            type="time"
                                                            value={scheduleTime}
                                                            onChange={(e) => setScheduleTime(e.target.value)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Submit Button */}
                                        <Button
                                            variant="success"
                                            onClick={handleSubmit}
                                            disabled={!broadcastName || !selectedTemplate || selectedContacts.length === 0}
                                        >
                                            Create Broadcast
                                        </Button>
                                    </Col>

                                    {/* Template Preview */}

                                </Row>
                            </Form>

                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6}>
                    {showTemplatePreview && renderTemplatePreview()}
                </Col>
            </Row>

            {/* Template Modal */}
            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="xl">
                <Modal.Header closeButton />
                <Modal.Body>
                    <AddNewTemplate />
                </Modal.Body>
            </Modal>

            {/* Contact Modal */}
            <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="lg">
                <Modal.Header className="d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #0eb7eb, #167491)' }} >
                    <Modal.Title className='text-white'>Select Contacts</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* <Form.Control
                        type="text"
                        placeholder="Search contacts..."
                        value={groupFilter}
                        onChange={(e) => setGroupFilter(e.target.value)}
                        className="mb-3"
                    /> */}
                    <Form.Control
                        type="text"
                        placeholder="Search contacts..."
                        value={groupFilter}
                        onChange={(e) => {
                            setGroupFilter(e.target.value);
                            setCurrentPage(1);   // 👈 IMPORTANT FIX
                        }}
                        className="mb-3"
                    />

                    <Table striped bordered hover>

                        <thead>
                            <tr>
                                <th>
                                    <Form.Check
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                const newContacts = currentContacts.filter(
                                                    contact => !selectedContacts.some(c => c.contact_id === contact.contact_id)
                                                );
                                                setSelectedContacts(prev => [...prev, ...newContacts]);
                                            } else {
                                                setSelectedContacts(prev =>
                                                    prev.filter(contact =>
                                                        !currentContacts.some(c => c.contact_id === contact.contact_id)
                                                    )
                                                );
                                            }
                                        }}
                                    />
                                </th>
                                <th>Name</th>
                                <th>Phone Number</th>
                                <th>Group</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentContacts.map(contact => (
                                <tr key={contact.contact_id}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedContacts.some(c => c.contact_id === contact.contact_id)}
                                            onChange={() => handleContactSelect(contact)}
                                        />
                                    </td>
                                    <td>{contact.contact_name}</td>
                                    <td>{contact.mobile_no}</td>
                                    <td>{contact.group_name}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* Pagination Controls */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {currentPage} of {totalPages || 1}
                        </span>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={currentPage === totalPages || totalPages === 0}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                        >
                            Next
                        </Button>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowContactModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={() => setShowContactModal(false)}>
                        Save Selection
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Broadcast;